import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import { animations, colors, mixins } from '../../../core/styles/theme/default';
import { LinkContent } from '../partials';
import { LinkProps } from '../Link';
import { StyledBackground, StyledShadow } from '../partials/LinkContent';
import classNames from 'classnames';
import { ImageInterface } from '~/types';

const StyledLink = styled(LinkContent)(
  ({
    isSmall,
    isLarge,
    halfImage,
    halfImageMobile,
    backgroundImage,
    theme,
  }: {
    isSmall?: boolean;
    isLarge?: boolean;
    halfImage?: boolean;
    halfImageMobile?: boolean;
    backgroundImage?: ImageInterface;
    theme?: Theme;
  }) => css`
    display: flex;
    justify-content: space-between;
    padding: ${rem(24)};
    border-radius: ${rem(4)};
    color: ${colors.white};
    transition: all ${animations.default};
    transition-property: box-shadow;
    padding-top: 100%;
    overflow: hidden;
    background-color: ${colors.nightBlue700};
    font-weight: 400;

    ${backgroundImage &&
    css`
      background-image: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.83) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    `}

    ${StyledShadow} {
      &::before {
        position: absolute;
        content: '';
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: -1;
        transition: opacity ${animations.default};
        opacity: 0;
        background-image: linear-gradient(
          360deg,
          rgba(21, 33, 61, 0.75) 0%,
          rgba(74, 95, 143, 0) 100%
        );
      }
    }

    ${StyledBackground} {
      background: transparent;
    }

    ${isLarge &&
    css`
      padding-top: 50%;

      ${mixins.MQ('medium')} {
        padding-top: 100%;
      }

      ${mixins.MQ('large')} {
        padding-top: 120%;
      }
    `}

    ${isSmall &&
    !halfImage &&
    !halfImageMobile &&
    css`
      justify-content: flex-start;
      padding: 50% ${rem(16)} ${rem(16)};

      ${mixins.MQ('large')} {
        padding-top: 100%;
      }
    `}

    ${halfImage &&
    css`
      border-radius: 0;
      justify-content: flex-start;
      padding: 50% ${rem(16)} ${rem(16)};

      ${mixins.MQ('large')} {
        padding-top: 50%;
      }
    `}

    ${halfImageMobile &&
    css`
      border-radius: 0;
      justify-content: flex-start;
      padding: 50% ${rem(16)} ${rem(16)};

      ${mixins.MQ('medium')} {
        padding-top: 100%;
      }
    `}

    > div:first-of-type {
      transition: background 1320ms linear, opacity 2000ms ease-out;
      background-color: transparent;
    }

    &:hover,
    &:focus,
    &.active {
      ${StyledShadow} {
        &::before {
          opacity: 1;
        }
      }
    }

    &:active {
      > div:first-of-type {
        background: ${colors.blue200};
        opacity: 0.4;
        mix-blend-mode: difference;
      }
    }

    .label {
      z-index: 3;
    }

    svg {
      margin-left: ${rem(24)};
      flex-shrink: 0;

      ${isSmall &&
      css`
        ${mixins.MQ('large')} {
          margin-left: ${rem(24)};
        }
      `}
    }

    ${halfImage &&
    css`
      img {
        object-position: 50% 0;
      }
    `}

    img {
      ${halfImage ||
      (halfImageMobile &&
        css`
          object-position: 50% 0;
        `)}
      ${mixins.MQ('medium')} {
        ${halfImageMobile &&
        css`
          object-position: 50% 50%;
        `}
      }
    }

    ${theme?.stylesExtension?.link?.emphasizedImage?.(!!backgroundImage)}
  `,
);

const LinkEmphasizedImage = ({
  iconAfter = 'AnimatedIconArrowRight',
  className,
  ...rest
}: LinkProps) => {
  return (
    <StyledLink
      iconAfter={iconAfter}
      className={classNames('animate', className)}
      {...rest}
    />
  );
};

export default LinkEmphasizedImage;
